import { useGetActivity, useGetAthletesInActivity } from 'api';
import ControlledAccordion from 'reporting/components/controlled-accordion/cordion';
import { ActivityStatusRow } from './activity-status-row';
import { ActivityStatusResponse, getStatusVariant } from './activity-status-util';
import { AthletesStatusRow } from './athletes-status-row';
import styles from './processing-status.module.css';
import { useEffect } from 'react';
import { isDevEnvironment } from 'shared';
import { useAthleteStatusGroups } from './hooks/useAthleteStatusGroups';
import { useGetActivityAthletesStatus } from 'api/baker/get';

type ActivityStatusElementProps = {
    s: ActivityStatusResponse;
    i: number;
    autoFocusedIndex: number;
    handleHideRow: (arg0: string) => void;
};

const ActivityStatusElement = ({ s, i, autoFocusedIndex, handleHideRow }: ActivityStatusElementProps) => {
    const wrapperStyles = {
        borderBottom: `1px solid var(--sys-outline-variant)`,
        paddingTop: i === 0 ? '6px' : '14px',
        paddingBottom: '14px',
    };

    let {
        data: activity,
        isLoading: activityLoading,
        isError: activityError,
        isSuccess: activitySuccess,
    } = useGetActivity(s.activityId);

    // test hiding an activity ('Friendly Match 3')in dev mode only
    if (isDevEnvironment && s.activityId === '7f00b666-f5b6-4110-b4e3-6b1f81e26876') {
        activity = undefined;
        activityLoading = false;
        activityError = false;
        activitySuccess = true;
    }

    // athlete details are only needed for the accordion (missing/synced lists)
    const { data: athletesInActivity, isLoading: athletesLoading } = useGetAthletesInActivity(s.activityId, [
        'device_info',
    ]);

    const activityAthletesCount = Array.isArray(athletesInActivity) ? athletesInActivity.length : 0;

    const statusVariant = getStatusVariant(s.status, activityAthletesCount);
    const processedWithMissingAthletes = statusVariant?.startsWith('processedWithMissing-') ?? false;
    const noFilesSynced = statusVariant === 'noFilesSyncedError';

    // this query is invalidated when activity statuses change, but we refetch conditionally when activity is 'baked with missing athletes'
    const { data: athleteStatuses, isLoading: athleteStatusesLoading } = useGetActivityAthletesStatus(
        s.activityId,
        processedWithMissingAthletes,
        processedWithMissingAthletes,
        isDevEnvironment,
    );

    const mainDataLoaded = !activityLoading && !athletesLoading;
    const waitingForAthleteStatuses = processedWithMissingAthletes && athleteStatusesLoading;
    const isReady = mainDataLoaded && !waitingForAthleteStatuses;

    useEffect(() => {
        // hide if API call failed, or succeeded but name is n/a
        const noName = activitySuccess && !activity?.name;

        if (mainDataLoaded && (activityError || noName)) {
            handleHideRow(s.activityId);
        }
    }, [activity?.name, s.activityId, handleHideRow, activityError, activitySuccess, mainDataLoaded]);

    // get grouped athletes based on processing status, plus unmapped athlete statuses
    const groups = useAthleteStatusGroups(athletesInActivity ?? [], athleteStatuses);

    // Determine if an accordion should be rendered, based on the computed variant and available athletes data
    const anyActivityAthletes = activityAthletesCount > 0;
    const anyMissingAthletes = Array.isArray(groups.missing) && groups.missing.length > 0;
    const showMissing = processedWithMissingAthletes && anyMissingAthletes; // on baked with missing
    const showAll = noFilesSynced && anyActivityAthletes; // on files not synced
    const shouldRenderAccordion = showAll || showMissing;

    if (!isReady) {
        return null;
    }

    const headerElement = (
        <ActivityStatusRow
            activityStatus={s}
            activityAthletesCount={activityAthletesCount}
            activity={activity}
            testId={`activity-status-row-${i}`}
        />
    );

    if (!shouldRenderAccordion) {
        return (
            <li className={styles['activity-row-wrapper']} style={wrapperStyles}>
                {headerElement}
                <div className="w-6" /> {/* spacer to match alignment with accordion (which has icon) */}
            </li>
        );
    }

    return (
        <li className="w-full block list-none">
            <ControlledAccordion
                id={s.activityId}
                headerElement={headerElement}
                contentElement={
                    <AthletesStatusRow
                        activityAthletes={athletesInActivity ?? []}
                        groups={groups}
                        showMissing={showMissing}
                        showAll={showAll}
                        testId={`athletes-status-row-${i}`}
                    />
                }
                trigger="header"
                headerWrapperStyles={{ cursor: 'pointer' }}
                isOpen={i === autoFocusedIndex}
                wrapperStyles={wrapperStyles}
            />
        </li>
    );
};

export default ActivityStatusElement;
